import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const money = (value) => value == null
  ? "Not allocated"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

function Section({ title, note, children }) {
  return (
    <section className="rounded-xl border border-border-primary bg-bg-secondary" style={{ overflow: "hidden" }}>
      <div className="border-b border-border-primary px-5 py-4">
        <h3 className="text-text-primary text-sm font-semibold">{title}</h3>
        <p className="text-text-secondary text-xs mt-1">{note}</p>
      </div>
      {children}
    </section>
  );
}

export default function ResourceInventoryView({ manifest, stale }) {
  const foundation = manifest?.foundation || [];
  const resources = manifest?.resources || [];
  const assumptions = manifest?.assumptions || [];
  const scopeNames = Object.fromEntries(foundation.map((scope) => [scope.logical_id, scope.name]));

  return (
    <div className="absolute inset-0 overflow-auto" style={{ padding: "64px 16px 24px" }} data-testid="resource-inventory-view">
      <div className="mx-auto w-full max-w-screen-2xl space-y-4">
        <div>
          <h2 className="text-text-primary text-xl font-semibold tracking-tight">Resource inventory</h2>
          <p className="text-text-secondary text-sm mt-1">
            Foundation scopes and proposed names for each selected environment. This is a design proposal, not a deployment plan.
          </p>
        </div>
        {stale && (
          <p role="status" className="rounded-lg border px-4 py-3 text-sm"
            style={{ borderColor: "var(--color-warning)", color: "var(--color-warning)" }}
            data-testid="inventory-stale-warning">
            {manifest?.edit_warnings?.[0] || "The diagram has changes that could not be reconciled with the inventory. Regenerate before relying on its names or costs."}
          </p>
        )}
        {!manifest ? (
          <div className="rounded-xl border border-dashed border-border-secondary bg-bg-tertiary/30 p-8 text-center" data-testid="inventory-empty">
            <p className="font-mono text-sm text-text-secondary">No naming inventory for this build.</p>
            <p className="text-xs text-text-tertiary mt-2">Older builds need a new requirements-backed generation.</p>
          </div>
        ) : (
          <>
            {assumptions.length > 0 && (
              <Section title="Assumptions and defaults" note="Review these before using the proposal for implementation.">
                <ul className="px-5 py-3 space-y-2 text-xs text-text-secondary" data-testid="inventory-assumptions">
                  {assumptions.map((item) => (
                    <li key={item.field}><span className="font-mono text-text-primary">{item.field}</span>: {String(item.value)} — {item.reason}</li>
                  ))}
                </ul>
              </Section>
            )}
            <Section title="Foundation scopes" note="Reference means customer-owned; planned does not mean Guided Sentry will create it.">
              <Table aria-label="Foundation scopes" data-testid="foundation-inventory-table">
                <TableHeader><TableRow>
                  <TableHead>Scope</TableHead><TableHead>Name</TableHead><TableHead>Environment</TableHead><TableHead>Parent</TableHead><TableHead>Lifecycle</TableHead>
                </TableRow></TableHeader>
                <TableBody>{foundation.map((scope) => (
                  <TableRow key={scope.logical_id} data-testid="foundation-inventory-row">
                    <TableCell className="font-mono text-xs">{scope.kind.replaceAll("_", " ")}</TableCell>
                    <TableCell className="font-mono text-xs">{scope.name}</TableCell>
                    <TableCell className="font-mono text-xs">{scope.environment || "Global"}</TableCell>
                    <TableCell className="font-mono text-xs">{scope.parent_id ? scopeNames[scope.parent_id] || scope.parent_id : "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{scope.lifecycle}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </Section>
            <Section title={`Resources (${resources.length})`}
              note={`${manifest.cost_estimate_complete === false ? "Known concept costs" : "Concept estimate"}: ${money(manifest.concept_monthly_cost_total)}/mo for the design; not a per-environment bill. Names follow the workload–environment–location–type–instance convention. Format checks do not verify global availability or IaC deployability.`}>
              <Table aria-label="Resource inventory" data-testid="resource-inventory-table">
                <TableHeader><TableRow>
                  <TableHead>Service</TableHead><TableHead>Logical name</TableHead><TableHead>Proposed physical name</TableHead><TableHead>Name rule</TableHead><TableHead>Env</TableHead><TableHead>Region</TableHead><TableHead>Scope</TableHead><TableHead>Est. monthly</TableHead><TableHead>Readiness</TableHead>
                </TableRow></TableHeader>
                <TableBody>{resources.map((resource) => (
                  <TableRow key={resource.logical_id} data-testid="resource-inventory-row">
                    <TableCell className="text-xs min-w-[150px]">
                      {resource.configuration?.display_name || resource.provider_service}
                      {resource.configuration?.display_name &&
                        resource.configuration.display_name !== resource.provider_service && (
                        <span className="block text-text-tertiary mt-1">{resource.provider_service}</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{resource.logical_name}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{resource.deploy_name}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{resource.name_rule_status === "verified_format" ? "Format checked" : "Not checked"}</TableCell>
                    <TableCell className="font-mono text-xs">{resource.environment}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{resource.region}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{scopeNames[resource.scope_id] || resource.scope_id}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{money(resource.estimated_monthly_cost)}</TableCell>
                    <TableCell className="text-xs min-w-[180px]">
                      <span className="font-mono capitalize">{resource.deployability}</span>
                      <span className="block text-text-tertiary mt-1">{resource.warnings?.join("; ")}</span>
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
